'use client';

/**
 * CoursePanel (T10d) — "one list, two heights" find_courses renderer.
 *
 * Replaces CourseBrowser/CourseTile (V2). Same collapsed-by-default shape
 * (top 3 + "see all") but the expanded state grows the SAME panel in place
 * on desktop/tablet, and only pops a dedicated full-screen overlay on
 * mobile — where there isn't room to grow a chat-surface-embedded panel
 * without shoving the conversation off screen.
 *
 * Props match the v1/V2 interface ({ courses: Course[] }) so the tool
 * registry can swap this in without changes to MessageBlock.
 */

import React, { memo, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronLeft } from 'lucide-react';
import type { Course } from '@/types/course';
import { cn } from '@/lib/utils';
import {
  CHIP_KEY,
  deriveChips,
  filterByChip,
  groupCourses,
  sortSoonest,
} from '@/lib/course-grouping';
import { CountPill } from '@/components/assistant/primitives/count-pill';
import { FilterChips } from '@/components/assistant/primitives/filter-chips';
import CourseRow from './course-row';

interface CoursePanelProps {
  courses: Course[];
  /** What the user SEARCHED (zip or "City, ST") — from the tool-call
   *  arguments forwarded on tool_end. Preferred for the header; absent on
   *  restored history → fall back to the results' own location. */
  searchedLocation?: string;
  className?: string;
}

const COLLAPSED_VISIBLE = 3;
/** Past this many filtered rows, the expanded list region caps its height
 *  and scrolls internally instead of pushing the chat surface arbitrarily
 *  tall. */
const SCROLL_CAP_THRESHOLD = 7;
/** Matches Tailwind's `sm` breakpoint (640px) — used to decide whether the
 *  mobile full-screen overlay (vs. desktop in-place growth) should hold
 *  body scroll. */
const MOBILE_MEDIA_QUERY = '(max-width: 639.98px)';

const GROUP_HEADER_COLOR = { warm: '#B0863C', blue: '#5A7BA8' } as const;

export const CoursePanel = memo(function CoursePanel({
  courses,
  searchedLocation,
  className,
}: CoursePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [activeChip, setActiveChip] = useState<string>(CHIP_KEY.ALL);

  // Every result renders, mixed titles included: multi-location searches
  // without a course_title legitimately merge different programs (FB33), and
  // each row shows its own title. The old V2 convention of filtering to the
  // first course's title hid every other program — and with it, whole cities
  // — from a merged result.
  const ordered = useMemo(
    () => (courses && courses.length > 0 ? sortSoonest(courses) : []),
    [courses]
  );

  const chips = useMemo(() => deriveChips(ordered), [ordered]);
  const filtered = useMemo(
    () => filterByChip(ordered, activeChip),
    [ordered, activeChip]
  );

  // Freeze "now" for the life of this panel instance rather than recomputing
  // per render — the "This weekend" bucket shouldn't jump groups mid-session
  // if a chat is left open across a midnight boundary.
  const [now] = useState(() => new Date());
  const groups = useMemo(() => groupCourses(filtered, now), [filtered, now]);

  // Lock body scroll only while the MOBILE full-screen overlay is actually
  // showing — the desktop/tablet expanded view grows in place and must not
  // block scrolling the rest of the chat. Both variants exist in the DOM
  // simultaneously (toggled via Tailwind breakpoints), so viewport width has
  // to be checked in JS to know which one is really "open".
  useEffect(() => {
    if (!expanded) return;
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const applyLock = (isMobile: boolean) => {
      document.body.style.overflow = isMobile ? 'hidden' : '';
    };
    applyLock(mql.matches);
    const handleChange = (e: MediaQueryListEvent) => applyLock(e.matches);
    mql.addEventListener('change', handleChange);
    return () => {
      document.body.style.overflow = '';
      mql.removeEventListener('change', handleChange);
    };
  }, [expanded]);

  if (ordered.length === 0) return null;

  // T10d header: "Part 1 courses near 07029" — program name without the
  // "Art of Living " brand prefix (the panel lives inside a branded app),
  // anchored to what the user SEARCHED (tool_end.arguments), falling back to
  // the results' own location for restored history / older backends. A
  // mixed-title merge (title-less multi-location search) gets the generic
  // "Courses near …" — naming one program over a mixed list would misstate
  // what's shown.
  const first = ordered[0];
  const uniformTitle = ordered.every((c) => c.title === first.title);
  const programName = (first.title ?? '').replace(/^Art of Living\s+/i, '');
  const headerPrefix =
    uniformTitle && programName ? `${programName} courses` : 'Courses';
  const nearLabel =
    searchedLocation ||
    first.locationPostalCode ||
    first.city ||
    first.locationCity;
  const headerTitle = nearLabel
    ? `${headerPrefix} near ${nearLabel}`
    : `${headerPrefix} near you`;
  const hiddenCount = filtered.length - COLLAPSED_VISIBLE;
  const showScrollCap = filtered.length >= SCROLL_CAP_THRESHOLD;

  // Shared by both expanded variants. The mobile overlay already scrolls at
  // the page level (flex-1 overflow-y-auto on its own container below), so
  // it renders this bare — only the desktop in-place variant wraps it in the
  // max-h/overflow scroll cap, to avoid nesting two scroll containers.
  const groupList = (
    <div className="flex flex-col gap-[16px]">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-2.5">
          <div
            className="text-[11.5px] font-bold uppercase"
            style={{
              letterSpacing: '.08em',
              color: GROUP_HEADER_COLOR[group.accent],
            }}
          >
            {group.label}
          </div>
          <div className="flex flex-col gap-2.5">
            {group.courses.map((course) => (
              <CourseRow
                key={course.id ?? course.url}
                course={course}
                showProgram={!uniformTitle}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Collapsed (all breakpoints) / desktop-tablet expanded-in-place */}
      <div
        className={cn(
          'flex flex-col gap-[16px] rounded-[20px] border-[1px] border-[#f0e7da] bg-white px-[20px] py-[18px]',
          'shadow-[0_1px_4px_rgba(0,0,0,0.04)]',
          expanded && 'hidden sm:flex',
          className
        )}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Design: title left, count pill flush RIGHT of the header row */}
          <h3 className="min-w-0 truncate font-['Work_Sans'] text-[15.5px] font-semibold leading-snug text-[#31364e]">
            {headerTitle}
          </h3>
          {!expanded && <CountPill count={filtered.length} />}
          {expanded && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="flex shrink-0 items-center gap-1 font-['Work_Sans'] text-[13px] font-semibold text-[#8a7c68]"
            >
              Collapse
              <ChevronUp className="size-3.5" />
            </button>
          )}
        </div>

        {chips.length > 1 && (
          <div className="flex items-center gap-2">
            <FilterChips
              chips={chips}
              active={activeChip}
              onSelect={setActiveChip}
              className="min-w-0 flex-1"
            />
            {/* Sort affordance (T10d) — static label: the list IS sorted
                soonest-first (sortSoonest above); no other orders exist yet. */}
            {expanded && (
              <span className="flex shrink-0 items-center gap-1 font-['Work_Sans'] text-[13px] font-semibold text-[#8a7c68]">
                Soonest
                <ChevronDown className="size-3.5" />
              </span>
            )}
          </div>
        )}

        {!expanded ? (
          <div className="flex flex-col gap-2.5">
            {filtered.slice(0, COLLAPSED_VISIBLE).map((course) => (
              <CourseRow
                key={course.id ?? course.url}
                course={course}
                showProgram={!uniformTitle}
              />
            ))}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className={cn(
                  'flex h-11 items-center justify-center gap-1.5 rounded-[14px] border-[1px] border-[#eadfce] bg-[#FFFCF8]',
                  "font-['Work_Sans'] text-[14px] font-semibold text-[#5b6072] transition-colors hover:bg-[#F6EEE3]"
                )}
              >
                See all {filtered.length}
                <ChevronUp className="size-4" />
              </button>
            )}
          </div>
        ) : (
          <div
            className={cn(
              showScrollCap && 'max-h-[520px] overflow-y-auto pr-1'
            )}
          >
            {groupList}
          </div>
        )}
      </div>

      {/* Mobile full-screen overlay, expanded state only */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#FFFAF5] sm:hidden">
          <div className="sticky top-0 z-10 flex flex-col gap-[12px] border-b border-[#f0e7da] bg-[#FFFAF5] px-[16px] pb-[12px] pt-[16px]">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Collapse course list"
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-[#5b6072]"
              >
                <ChevronLeft className="size-5" />
              </button>
              <h3 className="flex-1 truncate font-['Work_Sans'] text-[15.5px] font-semibold leading-snug text-[#31364e]">
                {headerTitle}
              </h3>
              <CountPill count={filtered.length} />
            </div>
            {chips.length > 1 && (
              <div className="overflow-x-auto">
                <FilterChips
                  chips={chips}
                  active={activeChip}
                  onSelect={setActiveChip}
                  className="flex-nowrap"
                />
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-[16px] py-[16px]">
            {groupList}
          </div>
        </div>
      )}
    </>
  );
});

export default CoursePanel;
